import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User, AuthTokens, TenantPermissions, AuthTenant } from '@/shared/types';
import { authApi } from '@/features/auth/api/auth.api';
import { operatorApi } from '@/features/operator/api/operator.api';
import { getApiErrorMessage } from '@/lib/api-error';
import { refreshAccessToken, resetAuthSessionState } from '@/shared/api/client';
import { defaultPlanFeaturesFallback, pairGrantedMenus } from '@/config/menus';
import { i18n } from '@/i18n';
import { CSRF_COOKIE_NAME } from '@/shared/auth/cookies';
import { configureApiAuthSession } from '@/shared/api/client';

function defaultPermissions(): TenantPermissions {
  return defaultPlanFeaturesFallback();
}

function hasAuthBootstrapCookie() {
  if (typeof document === 'undefined') return false;
  const prefix = `${encodeURIComponent(CSRF_COOKIE_NAME)}=`;
  return document.cookie
    .split('; ')
    .some((value) => value.startsWith(prefix));
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const tenant = ref<AuthTenant | null>(null);
  const permissions = ref<TenantPermissions>(defaultPermissions());
  const accessToken = ref<string | null>(null);
  const loading = ref(false);
  const authInitialized = ref(false);
  let authCheckPromise: Promise<void> | null = null;

  const isAuthenticated = computed(() => !!accessToken.value);
  const isImpersonating = computed(() => !!user.value?.impersonating);

  function setTokens(tokens: AuthTokens) {
    resetAuthSessionState();
    accessToken.value = tokens.accessToken;
  }

  function setUser(userData: User) {
    user.value = userData;
    localStorage.setItem('user', JSON.stringify(userData));
  }

  function setTenant(tenantData: AuthTenant | null) {
    tenant.value = tenantData;
    if (tenantData) {
      localStorage.setItem('tenant', JSON.stringify(tenantData));
    } else {
      localStorage.removeItem('tenant');
    }
  }

  function setPermissions(perms: TenantPermissions) {
    const next = {
      ...perms,
      menus: pairGrantedMenus(perms.menus || []),
    };
    permissions.value = next;
    localStorage.setItem('permissions', JSON.stringify(next));
  }

  function applyAuthPayload(data: {
    user: User;
    tenant?: AuthTenant | null;
    permissions?: TenantPermissions;
    accessToken?: string;
  }) {
    if (data.accessToken) {
      setTokens({ accessToken: data.accessToken, expiresIn: 0 });
    }
    setUser(data.user);
    if (data.tenant !== undefined) {
      setTenant(data.tenant);
    }
    if (data.permissions) {
      setPermissions(data.permissions);
    }
  }

  async function fetchMe() {
    if (!accessToken.value) return;
    try {
      const response = await authApi.getCurrentUser();
      const data = response.data.data;
      if (data?.user) {
        setUser(data.user);
      }
      if (data?.tenant !== undefined) {
        setTenant(data.tenant);
      }
      if (data?.permissions) {
        setPermissions(data.permissions);
      }
    } catch {
      // 忽略刷新失败，保留本地缓存
    }
  }

  async function login(email: string, password: string, tenantId?: string) {
    loading.value = true;
    try {
      const response = await authApi.login({ email, password, tenantId });
      if (!response.data.success) {
        return { requiresMfa: false as const, ok: false };
      }
      const data = response.data.data!;
      if (data.requiresMfa) {
        return {
          requiresMfa: true as const,
          ok: true,
          mfaToken: data.mfaToken,
          user: data.user,
        };
      }
      const { user: userData, accessToken, permissions: perms, tenant: tenantData } = data as any;
      setTokens({ accessToken, expiresIn: 0 });
      setUser(userData);
      if (tenantData !== undefined) setTenant(tenantData);
      if (perms) setPermissions(perms);
      return { requiresMfa: false as const, ok: true };
    } catch (error: unknown) {
      throw new Error(getApiErrorMessage(error, i18n.global.t('auth.loginFailed')));
    } finally {
      loading.value = false;
    }
  }

  async function verifyMfa(mfaToken: string, code: string) {
    loading.value = true;
    try {
      const response = await authApi.verifyMfa({ mfaToken, code });
      const data = response.data.data!;
      if (!data || data.requiresMfa) {
        throw new Error(i18n.global.t('auth.mfaFailed'));
      }
      const { user: userData, accessToken, permissions: perms, tenant: tenantData } = data as any;
      setTokens({ accessToken, expiresIn: 0 });
      setUser(userData);
      if (tenantData !== undefined) setTenant(tenantData);
      if (perms) setPermissions(perms);
      return true;
    } catch (error: unknown) {
      throw new Error(getApiErrorMessage(error, i18n.global.t('auth.verificationFailed')));
    } finally {
      loading.value = false;
    }
  }

  function applyLoginData(data: {
    requiresMfa: false;
    user: User;
    tenant?: AuthTenant | null;
    accessToken: string;
    permissions?: TenantPermissions;
  }) {
    setTokens({
      accessToken: data.accessToken,
      expiresIn: 0,
    });
    setUser(data.user);
    if (data.tenant !== undefined) setTenant(data.tenant);
    if (data.permissions) setPermissions(data.permissions);
  }

  async function impersonateTenant(tenantId: string) {
    loading.value = true;
    try {
      const response = await operatorApi.impersonateTenant(tenantId);
      const data = response.data.data;
      if (!data?.accessToken) {
        throw new Error(i18n.global.t('operatorTenants.impersonate.failed'));
      }
      applyAuthPayload(data);
      return true;
    } catch (error: unknown) {
      throw new Error(
        getApiErrorMessage(error, i18n.global.t('operatorTenants.impersonate.failed')),
      );
    } finally {
      loading.value = false;
    }
  }

  async function exitImpersonation() {
    loading.value = true;
    try {
      const response = await authApi.exitImpersonation();
      const data = response.data.data;
      if (!data?.accessToken) {
        throw new Error(i18n.global.t('impersonation.exitFailed'));
      }
      applyAuthPayload(data);
      return true;
    } catch (error: unknown) {
      throw new Error(
        getApiErrorMessage(error, i18n.global.t('impersonation.exitFailed')),
      );
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      await authApi.logout();
    } catch {
      // 忽略错误
    } finally {
      user.value = null;
      tenant.value = null;
      permissions.value = defaultPermissions();
      accessToken.value = null;
      localStorage.removeItem('user');
      localStorage.removeItem('tenant');
      localStorage.removeItem('permissions');
    }
  }

  function clearStoredAuth() {
    user.value = null;
    tenant.value = null;
    permissions.value = defaultPermissions();
    accessToken.value = null;
    localStorage.removeItem('user');
    localStorage.removeItem('tenant');
    localStorage.removeItem('permissions');
  }

  configureApiAuthSession({
    getAccessToken: () => accessToken.value,
    setTokens,
    setUser: (userData) => setUser(userData as User),
    setTenant: (tenantData) => setTenant((tenantData as AuthTenant | null) ?? null),
    setPermissions: (perms) => setPermissions(perms as TenantPermissions),
    clearStoredAuth,
  });

  async function checkAuth() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    const storedUser = localStorage.getItem('user');
    const storedTenant = localStorage.getItem('tenant');
    const storedPermissions = localStorage.getItem('permissions');

    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser);
      } catch {
        localStorage.removeItem('user');
      }
    }

    if (storedTenant) {
      try {
        tenant.value = JSON.parse(storedTenant);
      } catch {
        localStorage.removeItem('tenant');
      }
    }

    if (storedPermissions) {
      try {
        const parsed = JSON.parse(storedPermissions) as TenantPermissions;
        permissions.value = {
          ...parsed,
          menus: pairGrantedMenus(parsed.menus || []),
        };
      } catch {
        localStorage.removeItem('permissions');
      }
    }

    const nextToken = hasAuthBootstrapCookie()
      ? await refreshAccessToken()
      : null;
    if (!nextToken) {
      clearStoredAuth();
    }
  }

  function ensureAuthChecked() {
    if (authInitialized.value) return Promise.resolve();
    if (!authCheckPromise) {
      authCheckPromise = checkAuth().finally(() => {
        authInitialized.value = true;
        authCheckPromise = null;
      });
    }
    return authCheckPromise;
  }

  function canAccessMenu(menuKey: string) {
    return permissions.value.menus.includes(menuKey);
  }

  function hasFeature(featureKey: string) {
    return Boolean(permissions.value.features[featureKey]);
  }

  return {
    user,
    tenant,
    permissions,
    accessToken,
    loading,
    authInitialized,
    isAuthenticated,
    isImpersonating,
    login,
    verifyMfa,
    logout,
    checkAuth,
    ensureAuthChecked,
    fetchMe,
    setTokens,
    setUser,
    setTenant,
    setPermissions,
    clearStoredAuth,
    applyLoginData,
    impersonateTenant,
    exitImpersonation,
    canAccessMenu,
    hasFeature,
  };
});
